// Copyright 2025, University of Maryland, All Rights Reserved
interface Problem {
  problem_number: string | number;
  problem_description: string;
  difficulty_level: number;
  estimated_time_minutes: number;
  concepts_tested?: string[];
  skills_required?: string[];
}

interface ProblemAnalysisProps {
  problems: Problem[];
  documentType?: string;
}

export function ProblemAnalysis({ problems, documentType }: ProblemAnalysisProps) {
  if (!problems || problems.length === 0) {
    return null;
  }

  // Check if this is a syllabus or similar document type (not exam/quiz/test)
  const isSyllabus = documentType?.toLowerCase().includes('syllabus');
  const shouldShowTime = !isSyllabus && documentType;

  const getDifficultyColor = (level: number) => {
   if (level >= 8) return 'bg-[#E21833] text-white';
    if (level >= 6) return 'bg-red-100 text-[#B91428]';
    if (level >= 4) return 'bg-gray-200 text-gray-800';
    return 'bg-[#FFD200] text-gray-900';
  };

  return (
    <div className="bg-white p-8 rounded-xl shadow-lg pdf-page-break-avoid">
      <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
        <span className="text-3xl mr-3">🔍</span>
        Problem Analysis ({problems.length})
      </h2>

      <div className="space-y-4">
        {problems.map((problem, index) => (
          <div key={index} className="bg-gray-50 p-6 rounded-lg">
              <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-semibold text-gray-800">
                {isSyllabus ? 'Section' : 'Problem'} {problem.problem_number}
              </h3>
              <div className="flex space-x-2">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getDifficultyColor(problem.difficulty_level)}`}>
                  {isSyllabus ? 'Completeness' : 'Difficulty'}: {problem.difficulty_level}/10
                </span>
                {shouldShowTime && problem.estimated_time_minutes && (
                  <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                    {problem.estimated_time_minutes} min
                  </span>
                )}
              </div>
            </div>

            <div className="mb-4">
              <p className="text-gray-700 text-sm">{problem.problem_description}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-600">
                  {isSyllabus ? 'Topics Covered:' : 'Concepts Tested:'}
                </span>
                <p className="text-gray-800">{problem.concepts_tested?.join(', ') || 'N/A'}</p>
              </div>
              {problem.skills_required && problem.skills_required.length > 0 && (
                <div>
                  <span className="font-medium text-gray-600">Skills Required:</span>
                  <p className="text-gray-800">{problem.skills_required.join(', ')}</p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
